import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router'
import './index.css'
import { AuthProvider } from './app/auth-provider'
import { AutoReinjectStashedWork } from './app/auto-reinject-stashed-work'
import { AutoRescueHeldCaptures } from './app/auto-rescue-held-captures'
import { DevRoleSwitcher } from './app/dev-role-switcher'
import { SessionKeepAlive } from './app/session-keepalive'
import { AuthGatedPowerSyncProvider } from './hooks/powersync/provider'
import { router } from './app/router'
import {
  registerChunkReloadOnPreloadError,
  registerServiceWorkerUpdates,
} from './app/service-worker-updates'
import { DroppedChangeToast } from './capture/components/dropped-change-toast'
import { applyBranding } from './lib/brand'
import { initSentry } from './lib/sentry'

applyBranding()
initSentry()
// vite-plugin-pwa registers the service worker (autoUpdate); this adds
// the periodic update check an installed PWA needs so a new deploy is
// fetched + installed in the background and reaches field staff on the
// next load without a manual cache clear (no mid-capture forced reload —
// see service-worker-updates.ts).
registerServiceWorkerUpdates()
// Recover a lazy admin route whose old chunk was pruned when a new SW
// activated in the background (capture screens are static, so this never
// fires mid-capture). See service-worker-updates.ts.
registerChunkReloadOnPreloadError()

const root = document.getElementById('root')
if (!root) throw new Error('Root element not found')

createRoot(root).render(
  <StrictMode>
    <AuthProvider>
      {/*
       * AuthGatedPowerSyncProvider waits for AuthProvider to resolve to
       * 'authenticated' before mounting the underlying PowerSyncProvider.
       * That avoids a bootstrap-window race where the connector would
       * call /powersync/token, get a 401, and fall into its hardcoded
       * navigateTo('/auth/login') without preserving the deep-link
       * target — RequireAuth's redirect_to-aware redirect only fires
       * after the gated route mounts, which happens after auth resolves.
       * It is OUTSIDE the RouterProvider so once it does mount, the
       * whole tree can use PowerSync hooks.
       */}
      <AuthGatedPowerSyncProvider>
        <RouterProvider router={router} />
        {/*
         * DroppedChangeToast subscribes to the module-level upload-events
         * drop channel. It is mounted HERE — app-wide, a sibling of the
         * router and inside the PowerSync provider — rather than under the
         * /capture route, because the connector drains the upload queue
         * app-wide (this provider wraps the whole router). A permanent
         * drop can therefore fire while the user is on /admin, on the
         * login splash, or mid role-home redirect. The drop channel does
         * not buffer, so a subscriber scoped to /capture would miss those
         * events after the connector had already deleted the local row —
         * silent contractual data-loss, the exact failure this banner
         * exists to prevent. AuthGatedPowerSyncProvider renders its
         * children even before auth resolves, so this subscriber is live
         * from the first commit, before any upload can run. (Codex P2 on
         * PR #570.)
         */}
        <DroppedChangeToast />
        {/*
         * AutoRescueHeldCaptures fires the Phase 1 offline-capture rescue
         * automatically once the user is authenticated — no route, no UI.
         * Mounted app-wide here (sibling of the router, inside the PowerSync
         * provider) so it survives route changes and runs for any authed
         * role. It reads the LOCAL-only `orphaned_capture_items` table (no
         * sync handshake needed — only auth, for the POST) and ships held
         * counts to the isolated recovery inbox. Replaces the removed hidden
         * /recover-held-captures page so field staff never have to find a URL.
         */}
        <AutoRescueHeldCaptures />
        {/*
         * SessionKeepAlive slides the 24 h session cookie forward on
         * authenticated activity (POST /auth/refresh) so a long field shift
         * never crosses the expiry mid-work and 401-jams the upload queue
         * (F2/#808). Headless, app-wide, any authed role — mirrors
         * AutoRescueHeldCaptures. It gates on the authenticated status itself.
         */}
        <SessionKeepAlive />
        {/*
         * AutoReinjectStashedWork is the Tier-3 sibling: when a user signed out
         * while OFFLINE, their un-uploaded counts were stashed on-device (they
         * could not be POSTed). This re-injects that stash to the recovery inbox
         * once the SAME user is authenticated and online again, then clears it.
         * Scoped to the current user's id (shared-device safety). Headless. (#809)
         */}
        <AutoReinjectStashedWork />
        <DevRoleSwitcher />
      </AuthGatedPowerSyncProvider>
    </AuthProvider>
  </StrictMode>,
)
